import {
  safeGetModelArrayFromQuery,
  safeGetModelFromRef
} from '../model/getModel';
import {
  INVOICE_STATUSES_UNPAID,
  InvoiceModel
} from '../model/objects/invoiceModel';
import { EFirebaseContext, ERef } from '../types';
import { OrderInvoice, PublicNoticeInvoice } from '../types/invoices';
import { Order } from '../types/order';
import { ResponseOrError, wrapError, wrapSuccess } from '../types/responses';
import { getErrorReporter } from '../utils/errors';
import { ColumnService } from './directory';

export class InvoiceService {
  constructor(private context: EFirebaseContext) {}

  /**
   * A public notice invoice as a model, addressed by id. Integration code
   * works in models, so the ref never leaves this service.
   */
  public async getPublicNoticeInvoiceById(
    invoiceId: string
  ): Promise<ResponseOrError<InvoiceModel<PublicNoticeInvoice>>> {
    return this.getPublicNoticeInvoice(
      this.context.invoicesRef<PublicNoticeInvoice>().doc(invoiceId)
    );
  }

  public async getPublicNoticeInvoice(
    invoiceRef: ERef<PublicNoticeInvoice>
  ): Promise<ResponseOrError<InvoiceModel<PublicNoticeInvoice>>> {
    const { response: invoiceModel, error: invoiceError } =
      await safeGetModelFromRef(InvoiceModel, this.context, invoiceRef);
    if (invoiceError) {
      return wrapError(invoiceError);
    }
    if (!invoiceModel) {
      return wrapError(new Error(`Invoice with ID ${invoiceRef.id} not found`));
    }
    return wrapSuccess(invoiceModel as InvoiceModel<PublicNoticeInvoice>);
  }

  public async getByOrderAndVersion(
    orderRef: ERef<Order>,
    version: number,
    options: { allowVoidInvoice?: boolean } = {}
  ): Promise<ResponseOrError<InvoiceModel<OrderInvoice> | null>> {
    const invoiceQuery = this.context
      .invoicesRef<OrderInvoice>()
      .where('order', '==', orderRef)
      .where('orderVersion', '==', version);

    const { response: invoicesForOrder, error: invoiceError } =
      await safeGetModelArrayFromQuery(
        InvoiceModel,
        this.context,
        invoiceQuery
      );
    if (invoiceError) {
      getErrorReporter().logAndCaptureError(
        ColumnService.ORDER_MANAGEMENT,
        invoiceError,
        'Failed to get invoice from order',
        {
          orderId: orderRef.id,
          version: `${version}`
        }
      );
      return wrapError(invoiceError);
    }

    /**
     * Our linter does not like passing in a narrowed type (InvoiceModel<OrderInvoice>)
     * into `safeGetModelArrayFromQuery` above, this cast is necessary to get an
     * array that is properly typed as InvoiceModel<OrderInvoice>[]
     */
    const activeInvoices = (invoicesForOrder as InvoiceModel<OrderInvoice>[])
      // Never consider voided invoices that were never paid.
      .filter(
        invoice =>
          !(
            invoice.isVoided() &&
            INVOICE_STATUSES_UNPAID.includes(invoice.modelData.status)
          )
      )
      // Only include paid voided invoices if the option is set to allow it
      .filter(invoice => options.allowVoidInvoice || !invoice.isVoided());
    if (activeInvoices.length > 1) {
      const errorMessage =
        'Expected no more than 1 invoice for order and version';
      const invoiceIds = activeInvoices.map(invoice => invoice.id).join(', ');
      const error = Error(errorMessage);

      getErrorReporter().logAndCaptureError(
        ColumnService.PAYMENTS,
        error,
        errorMessage,
        {
          orderId: orderRef.id,
          version: `${version}`,
          invoiceIds
        }
      );

      return wrapError(error);
    }

    return wrapSuccess(activeInvoices[0] ?? null);
  }
}
